import { useEffect, useState, type ReactNode } from 'react'
import { PointsPrice } from '@/components/features/points/PointsPrice'
import { canRedeemNow, isFreeProduct, pricingModeOf } from '@/components/features/points/productPoints'
import { useTranslation } from 'react-i18next'
import { CoverImage } from '@/components/ui/cover-image'
import { usePriceLabel } from '@/components/features/products/usePriceLabel'
import { Check, Link2, Package, Wallet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { parseAllowedPaymentMethods } from '@/lib/checkout/paymentMethods'
import { PAYMENT_METHOD_ICONS, productCover } from '@/lib/checkout/productDisplay'
import type { PublicProduct } from '@/types/checkout'
import { useMemberEntry } from '@/lib/hooks/useMemberEntry'
import { SuspendedNotice } from '@/components/features/memberEntry/SuspendedNotice'
import { RichHtml } from '@/components/ui/rich-html'

/* Descriptions written in the app's form are plain text; the ones that came
   through the API or the assistant may be HTML. Plain text keeps its line
   breaks; HTML goes through the same sanitizer as every rich text in the app. */
const looksLikeHtml = (text: string) => /<\/?[a-z][\s\S]*>/i.test(text)

interface ProductDetailsDialogProps {
  product: PublicProduct | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** "Buy" was pressed: the catalog closes this and opens the purchase. */
  onBuy?: (product: PublicProduct) => void
  /** F-160: redeem with points (products that take points). */
  onRedeem?: (product: PublicProduct) => void
  /**
   * Replaces the payment block (methods, Buy, copy link) for an item not
   * bought on its own, such as a storefront add-on: its action comes from the
   * server's state for this viewer, and it has no link of its own.
   */
  action?: ReactNode
}

/**
 * One product, read before buying: cover, title, full description, price,
 * installments, the methods it takes and the CTA.
 *
 * A dialog over the listing rather than a route of its own: the catalog is
 * mounted both by `/products` and by every storefront space, and neither has
 * a sub-route for an item. The catalog keeps `?product={slug}` in the address
 * while this is open, so the link can be shared and lands right here.
 */
export function ProductDetailsDialog({ product, open, onOpenChange, onBuy, onRedeem, action }: ProductDetailsDialogProps) {
  const { t } = useTranslation(['checkout', 'products', 'points'])
  const priceLabel = usePriceLabel()
  const [copied, setCopied] = useState(false)
  const { suspended: salesClosed } = useMemberEntry()

  useEffect(() => {
    if (!open) setCopied(false)
  }, [open])

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* Clipboard refused (insecure context, permission): the address bar
         already holds the same link, so there is nothing else to offer. */
    }
  }

  const cover = product ? productCover(product) : null
  const methods = product ? parseAllowedPaymentMethods(product.allowedPaymentMethods) : []
  const soldOut = Boolean(product?.soldOut)
  const installments = Number(product?.maxInstallments) || 1
  const recurring = Boolean(product && (product.type === 'SUBSCRIPTION' || product.billingInterval))
  const description = product?.description?.trim() || ''
  /* F-160: points-only shows its price in points and redeems; points or money offers both. */
  const mode = product ? pricingModeOf(product) : 'MONEY'
  const pointsOnly = mode === 'POINTS'
  const free = product ? isFreeProduct(product) : false
  const canRedeem = Boolean(product && canRedeemNow(product))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-3xl overflow-y-auto p-4 sm:p-6">
        {product && (
          <div className="min-w-0 space-y-5" data-testid="product-details">
            <div className="relative aspect-[3/1] overflow-hidden rounded-lg">
              <CoverImage src={cover} alt="" icon={Package} iconClassName="h-10 w-10" className="h-full w-full object-cover" />
              {(product.isHighlighted || soldOut) && (
                <span
                  className={cn(
                    'absolute left-3 top-3 rounded-md px-2 py-0.5 text-xs font-medium',
                    soldOut ? 'theme-light bg-gray-900/80 text-white' : 'bg-primary text-white'
                  )}
                >
                  {soldOut ? t('purchase.soldOut') : t('purchase.featured')}
                </span>
              )}
            </div>

            <DialogHeader className="text-left">
              <DialogTitle className="text-2xl font-semibold leading-snug text-gray-900">{product.title}</DialogTitle>
              {product.shortDescription ? (
                <DialogDescription className="text-gray-600">{product.shortDescription}</DialogDescription>
              ) : (
                <DialogDescription className="sr-only">{t('purchase.details.srDescription')}</DialogDescription>
              )}
            </DialogHeader>

            <div className="grid min-w-0 gap-6 sm:grid-cols-[minmax(0,1fr)_16rem]">
              {/* What one consults or acts on goes to the side; on a phone it
                  comes before the description, so the CTA is not buried. */}
              <aside className="min-w-0 sm:order-2">
                <div className="space-y-4 rounded-xl border border-gray-200 p-4">
                  <div>
                    <p className="text-xs text-gray-500">{t('purchase.details.price')}</p>
                    {!pointsOnly && <p className="text-2xl font-semibold text-gray-900">
                      {priceLabel(product.price, product.currency)}
                      {recurring && (
                        <span className="ml-1 text-sm font-normal text-gray-500">
                          {t(product.billingInterval === 'YEARLY' ? 'perYear' : 'perMonth')}
                        </span>
                      )}
                    </p>}
                    <PointsPrice product={product} size="lg" className={pointsOnly ? undefined : 'mt-1'} />
                    {!recurring && !free && !pointsOnly && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        {installments > 1 && methods.includes('CREDIT_CARD')
                          ? t('purchase.details.upToInstallmentsCard', { count: installments })
                          : t('purchase.singlePayment')}
                      </p>
                    )}
                  </div>

                  {action ?? (<>
                    {methods.length > 0 && !free && !pointsOnly && (
                      <div>
                        <p className="mb-2 flex items-center gap-2 text-xs text-gray-500">
                          <Wallet className="h-4 w-4 text-gray-400" />
                          {t('purchase.details.paymentMethods')}
                        </p>
                        <ul className="space-y-1.5">
                          {methods.map((method) => {
                            const Icon = PAYMENT_METHOD_ICONS[method]
                            return (
                              <li key={method} className="flex items-center gap-2 text-sm text-gray-700">
                                <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                {t(`purchase.methods.${method}`)}
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    )}

                    {salesClosed ? (
                      /* F-86: the community is suspended; nothing new is sold. */
                      <div className="space-y-2" data-testid="product-details-unavailable">
                        <p className="text-center text-sm font-semibold text-gray-900">{t('common:memberEntry.unavailable')}</p>
                        <SuspendedNotice kind="sale" />
                      </div>
                    ) : soldOut ? (
                      <div className="rounded-lg bg-gray-100 p-3 text-center" data-testid="product-details-sold-out">
                        <p className="text-sm font-medium text-gray-900">{t('purchase.soldOut')}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{t('purchase.details.soldOutHint')}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {!pointsOnly && (
                          <Button
                            variant="secondary"
                            size="lg"
                            className="w-full"
                            onClick={() => onBuy?.(product)}
                            data-testid="product-details-buy"
                          >
                            {free ? t('products:freeClaim.cta') : t('purchase.buy')}
                          </Button>
                        )}
                        {mode !== 'MONEY' && onRedeem && (
                          <Button
                            variant={pointsOnly ? 'secondary' : 'outline'}
                            size="lg"
                            className="w-full"
                            onClick={() => onRedeem(product)}
                            disabled={!canRedeem}
                            data-testid="product-details-redeem"
                          >
                            {t('points:card.redeem')}
                          </Button>
                        )}
                      </div>
                    )}

                    <Button variant="ghost" size="sm" className="w-full text-gray-600" onClick={() => void copyLink()}>
                      {copied ? <Check className="mr-2 h-4 w-4" /> : <Link2 className="mr-2 h-4 w-4" />}
                      {copied ? t('purchase.details.linkCopied') : t('purchase.details.copyLink')}
                    </Button>
                  </>)}
                </div>
              </aside>

              <div className="min-w-0 sm:order-1">
                {description && (
                  <>
                    <h3 className="mb-2 font-semibold text-gray-900">{t('purchase.details.about')}</h3>
                    {looksLikeHtml(description) ? (
                      <RichHtml
                        className="prose prose-sm max-w-none break-words text-gray-600"
                        html={description}
                      />
                    ) : (
                      <p className="whitespace-pre-line break-words text-sm text-gray-600">{description}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
